/**
 * The AI row above each variant editor: rewrite with an instruction, regenerate
 * from the raw notes, ask for hashtags, or generate an on-brand image.
 *
 * Every action is a single request; while one is in flight the others stay
 * available but the running one shows its own spinner.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { Hash, ImagePlus, RefreshCw, Sparkles, Wand2 } from 'lucide-react';
import { PLATFORM_SPECS } from '../../../shared/platforms';
import type { PlatformId } from '../../../shared/types';
import { Button, DropdownItem, DropdownLabel, DropdownMenu, DropdownSeparator, Textarea } from '../ui';
import { cn } from '../../lib/utils';

export type AiAction = 'improve' | 'regenerate' | 'hashtags' | 'image';

export interface AiToolbarProps {
  platform: PlatformId;
  busy: AiAction | null;
  onImprove: (instruction: string) => void;
  onRegenerate: (instructions?: string) => void;
  onHashtags: () => void;
  onGenerateImage: () => void;
  disabled?: boolean;
  className?: string;
}

const IMPROVE_PRESETS: Record<'default' | 'blog', string[]> = {
  default: [
    'Make it punchier and lead with the hook',
    'Trim it so it fits comfortably under the limit',
    'Warmer and more conversational',
    'More professional, no hype',
    'End with a clear call to action',
    'Fix typos and tighten the grammar',
  ],
  blog: [
    'Add a stronger opening paragraph',
    'Break it into scannable sections with subheadings',
    'Add a short conclusion with a call to action',
    'Make the tone more concrete — swap adjectives for specifics',
    'Fix typos and tighten the grammar',
  ],
};

/** Menu of one-tap instructions plus a free-form box. */
function InstructionMenu({
  trigger,
  label,
  presets,
  placeholder,
  submitLabel,
  onSubmit,
  extra,
}: {
  trigger: ReactNode;
  label: string;
  presets: string[];
  placeholder: string;
  submitLabel: string;
  onSubmit: (instruction: string) => void;
  extra?: (close: () => void) => ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setText('');
      }}
      trigger={trigger}
      menuClassName="w-80"
    >
      {(close) => (
        <>
          <DropdownLabel>{label}</DropdownLabel>
          {presets.map((preset) => (
            <DropdownItem
              key={preset}
              onSelect={() => {
                onSubmit(preset);
                close();
              }}
            >
              {preset}
            </DropdownItem>
          ))}
          {extra?.(close)}
          <DropdownSeparator />
          <div className="space-y-2 p-2">
            <Textarea
              value={text}
              rows={2}
              maxRows={5}
              placeholder={placeholder}
              aria-label={label}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => {
                if (['ArrowDown', 'ArrowUp'].includes(event.key)) event.stopPropagation();
                if (event.key === 'Enter' && (event.metaKey || event.ctrlKey) && text.trim()) {
                  event.preventDefault();
                  onSubmit(text.trim());
                  close();
                }
              }}
              className="text-[13px]"
            />
            <div className="flex items-center justify-between gap-2">
              <span className="text-[11px] text-subtle">⌘↵ to apply</span>
              <Button
                size="xs"
                disabled={!text.trim()}
                onClick={() => {
                  onSubmit(text.trim());
                  close();
                }}
              >
                {submitLabel}
              </Button>
            </div>
          </div>
        </>
      )}
    </DropdownMenu>
  );
}

export function AiToolbar({
  platform,
  busy,
  onImprove,
  onRegenerate,
  onHashtags,
  onGenerateImage,
  disabled,
  className,
}: AiToolbarProps) {
  const spec = PLATFORM_SPECS[platform];
  const presets = spec.kind === 'blog' ? IMPROVE_PRESETS.blog : IMPROVE_PRESETS.default;

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      <span className="mr-0.5 inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-subtle">
        <Sparkles className="size-3.5" aria-hidden />
        AI
      </span>

      <InstructionMenu
        label="Rewrite this draft"
        presets={presets}
        placeholder="Tell the model what to change…"
        submitLabel="Rewrite"
        onSubmit={onImprove}
        trigger={
          <Button
            variant="secondary"
            size="xs"
            disabled={disabled}
            loading={busy === 'improve'}
            leftIcon={<Wand2 className="size-3.5" />}
          >
            Improve
          </Button>
        }
      />

      <InstructionMenu
        label="Regenerate from your notes"
        presets={[]}
        placeholder="Anything to steer it this time?"
        submitLabel="Regenerate"
        onSubmit={(instruction) => onRegenerate(instruction)}
        extra={(close) => (
          <DropdownItem
            icon={<RefreshCw />}
            onSelect={() => {
              onRegenerate();
              close();
            }}
          >
            Start over from the raw notes
          </DropdownItem>
        )}
        trigger={
          <Button
            variant="secondary"
            size="xs"
            disabled={disabled}
            loading={busy === 'regenerate'}
            leftIcon={<RefreshCw className="size-3.5" />}
          >
            Regenerate
          </Button>
        }
      />

      {spec.features.hashtags && (
        <Button
          variant="secondary"
          size="xs"
          disabled={disabled}
          loading={busy === 'hashtags'}
          onClick={onHashtags}
          leftIcon={<Hash className="size-3.5" />}
        >
          Hashtags
        </Button>
      )}

      {spec.features.images && (
        <Button
          variant="secondary"
          size="xs"
          disabled={disabled}
          loading={busy === 'image'}
          onClick={onGenerateImage}
          leftIcon={<ImagePlus className="size-3.5" />}
        >
          Generate image
        </Button>
      )}
    </div>
  );
}
